import {ReactNode, useRef, useState} from 'react';
import './DashboardShell.css';

/**
 * B56 (Issue #192 restart, docs/ai/INFORMATION_DENSITY_UX_PROPOSAL.md): shared
 * layout primitive so Mission Control, FenzSafe, and the attorney workspace
 * consume one shell instead of three divergent one-offs. Pure chrome -- no
 * app-specific content, no data fetching, no auth/tenant logic.
 */
export type DashboardNavItem = {
  id: string;
  label: string;
};

export type DashboardPrimaryAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

type DashboardShellProps = {
  /** aria-label for the whole shell's outer landmark, e.g. "Mission Control". */
  shellLabel: string;
  navItems: DashboardNavItem[];
  activeNavId: string;
  onNavSelect: (id: string) => void;
  /** aria-label for the nav landmark itself, defaults to "Primary". */
  navLabel?: string;
  /** Page title shown in the content header, next to the primary action. */
  title: string;
  primaryAction?: DashboardPrimaryAction;
  /** Global-action slot (search/help/notifications/settings/account) in the top utility bar. Only render what the consuming surface actually has today -- this slot does not itself add features. */
  utilityActions?: ReactNode;
  children: ReactNode;
};

/**
 * Roving-tabindex keyboard support for the nav list: Up/Down/Home/End move
 * focus without leaving the list to Tab through every item, matching the
 * admin-console nav convention the founder referenced. Enter/Space activate
 * via the button's own native behavior -- no extra handling needed for those.
 */
function useRovingNavFocus(itemCount: number) {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function focusIndex(nextIndex: number) {
    const clamped = Math.max(0, Math.min(itemCount - 1, nextIndex));
    setFocusedIndex(clamped);
    itemRefs.current[clamped]?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusIndex(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusIndex(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusIndex(0);
        break;
      case 'End':
        event.preventDefault();
        focusIndex(itemCount - 1);
        break;
      default:
        break;
    }
  }

  return {focusedIndex, setFocusedIndex, itemRefs, onKeyDown};
}

export function DashboardShell({shellLabel, navItems, activeNavId, onNavSelect, navLabel = 'Primary', title, primaryAction, utilityActions, children}: DashboardShellProps) {
  const [navOpen, setNavOpen] = useState(false);
  const {focusedIndex, setFocusedIndex, itemRefs, onKeyDown} = useRovingNavFocus(navItems.length);
  // Below the mobile breakpoint, closing the drawer applies display:none (DashboardShell.css) to
  // whichever nav item the user just activated by keyboard -- a browser silently drops focus to
  // <body> when its focused element is hidden this way. The toggle button stays visible/focusable
  // (it's what reopens the drawer), so it's the persistent target focus lands on instead.
  const navToggleRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="dashboard-shell" aria-label={shellLabel}>
      <header className="dashboard-shell-utility-bar">
        <button
          type="button"
          ref={navToggleRef}
          className="dashboard-shell-nav-toggle"
          aria-expanded={navOpen}
          aria-controls="dashboard-shell-nav"
          onClick={() => setNavOpen((open) => !open)}
        >
          <span className="dashboard-shell-nav-toggle-icon" aria-hidden="true">
            ☰
          </span>
          Menu
        </button>
        {utilityActions && <div className="dashboard-shell-utility-actions">{utilityActions}</div>}
      </header>

      <div className="dashboard-shell-body">
        <nav id="dashboard-shell-nav" aria-label={navLabel} className={navOpen ? 'dashboard-shell-nav dashboard-shell-nav-open' : 'dashboard-shell-nav'}>
          <ul className="dashboard-shell-nav-list" role="list">
            {navItems.map((item, index) => {
              const selected = item.id === activeNavId;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    ref={(node) => {
                      itemRefs.current[index] = node;
                    }}
                    className={selected ? 'dashboard-shell-nav-item dashboard-shell-nav-item-selected' : 'dashboard-shell-nav-item'}
                    aria-current={selected ? 'page' : undefined}
                    tabIndex={focusedIndex === index ? 0 : -1}
                    onFocus={() => setFocusedIndex(index)}
                    onKeyDown={(event) => onKeyDown(event, index)}
                    onClick={() => {
                      onNavSelect(item.id);
                      // Desktop never opens the drawer (the toggle that sets navOpen is itself
                      // only visible/clickable below the breakpoint), so this branch -- and the
                      // focus move it makes -- cannot fire during ordinary desktop roving-key use.
                      if (navOpen) {
                        setNavOpen(false);
                        navToggleRef.current?.focus();
                      }
                    }}
                  >
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="dashboard-shell-content">
          <div className="dashboard-shell-header">
            <h1 className="dashboard-shell-title">{title}</h1>
            {primaryAction && (
              <button type="button" className="dashboard-shell-primary-action" disabled={primaryAction.disabled} onClick={primaryAction.onClick}>
                {primaryAction.label}
              </button>
            )}
          </div>
          <main className="dashboard-shell-main">{children}</main>
        </div>
      </div>
    </div>
  );
}
